import React, { createContext, useContext, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const ApiContext = createContext();

export const useApi = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context;
};

export const ApiProvider = ({ children }) => {
  const { getIdToken, user } = useAuth();
  
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://your-api-gateway-url.amazonaws.com';

  const createApiClient = async () => {
    try {
      // Check if user is authenticated first
      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await getIdToken();

      // Check for impersonation token and append it to Authorization header
      const impersonationToken = sessionStorage.getItem('impersonationToken');
      let authHeader = `Bearer ${token}`;

      if (impersonationToken) {
        // Append impersonation token using pipe separator
        authHeader = `${authHeader}|IMPERSONATE|${impersonationToken}`;
        console.log('[API] Using impersonation token in Authorization header');
      }

      const headers = {
        'Authorization': authHeader,
        'Content-Type': 'application/json'
      };

      const apiClient = axios.create({
        baseURL: apiBaseUrl,
        headers: headers
      });

      return apiClient;
    } catch (error) {
      console.error('Failed to create API client:', error);
      throw error;
    }
  };

  const handleApiError = (error) => {
    if (error.code === 'ENOTFOUND' || error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
      throw new Error(`API server is not available. Please ensure the backend infrastructure is deployed. Details: ${error.message}`);
    } else if (error.response?.status === 401) {
      // Token expired or invalid
      throw new Error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      // Surface the backend's specific reason (e.g. "Out of scope: location 'X'") —
      // the generic line hid actionable detail from delegated managers.
      throw new Error(error.response?.data?.error || 'You do not have permission to access this resource');
    } else if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    } else {
      throw new Error(`API request failed: ${error.message} (Status: ${error.response?.status || 'Unknown'})`);
    }
  };

  // Helper function to check authentication
  const ensureAuthenticated = () => {
    if (!user) {
      throw new Error('User not authenticated');
    }
  };

  // API methods
  const getOverview = async () => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.get('/api/v1/overview');
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  const getTerminals = async (params = {}) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      
      // Add cache-busting timestamp to prevent stale results
      const requestParams = {
        ...params,
        _t: Date.now()
      };
      
      console.log('API: Fetching terminals with params:', requestParams);
      const response = await api.get('/api/v1/terminals', { params: requestParams });
      console.log('API: Received', response.data?.terminals?.length, 'terminals');
      
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  const getTerminalDetails = async (terminalId, params = {}) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.get(`/api/v1/terminals/${encodeURIComponent(terminalId)}`, { params });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  const getLocations = async () => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.get('/api/v1/businesses');
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  const updateLocationClosingPeriod = async (locationName, closingHour) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.put(`/api/v1/businesses/${encodeURIComponent(locationName)}/closing-period`, {
        closingHour: parseInt(closingHour)
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  const getLocationDetails = async (locationName) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.get(`/api/v1/businesses/${encodeURIComponent(locationName)}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  const getSettlementReport = async (params) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.post('/api/v1/settlement-report', params);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  const uploadFile = async (file) => {
    try {
      const api = await createApiClient();
      
      // For large files (>5MB), use presigned URL upload
      if (file.size > 5 * 1024 * 1024) {
        return await uploadFileWithPresignedUrl(api, file);
      } else {
        return await uploadFileDirectly(api, file);
      }
      
    } catch (error) {
      console.error('Upload failed:', error.message);
      handleApiError(error);
    }
  };

  const uploadFileDirectly = async (api, file) => {
    // Convert file to base64
    const fileContent = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; // Remove data:text/csv;base64, prefix
        resolve(base64);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });

    const uploadData = {
      fileName: file.name,
      fileContent: fileContent,
      fileSize: file.size
    };
    
    const response = await api.post('/api/v1/upload', uploadData);
    return response.data;
  };

  const uploadFileWithPresignedUrl = async (api, file) => {
    // Step 1: Get presigned URL
    const presignedResponse = await api.post('/api/v1/upload', {
      requestType: 'presignedUrl',
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    });
    
    const { presignedUrl, s3Key } = presignedResponse.data;
    
    // Step 2: Upload directly to S3
    const uploadResponse = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    });
    
    if (!uploadResponse.ok) {
      throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
    }
    
    // Step 3: Confirm upload with backend
    const confirmResponse = await api.post('/api/v1/upload', {
      requestType: 'confirmUpload',
      s3Key: s3Key,
      fileName: file.name,
      fileSize: file.size
    });
    
    return confirmResponse.data;
  };

  const getUploadHistory = async () => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.get('/api/v1/upload');
      return response.data;
    } catch (error) {
      if (error.code === 'ENOTFOUND' || error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
        // Return empty uploads when API is not available
        return { uploads: [] };
      }
      handleApiError(error);
    }
  };

  // User Management API methods
  const getUsers = async (params = {}) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.get('/api/v1/users', { params });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  const getUserById = async (userId) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.get(`/api/v1/users/${userId}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  const createUser = async (userData) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.post('/api/v1/users', userData);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  const updateUser = async (userId, userData) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.put(`/api/v1/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  const deleteUser = async (userId) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.delete(`/api/v1/users/${userId}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  const resetUserPassword = async (userId) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.post(`/api/v1/users/${userId}/reset-password`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  const recordLogin = async () => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.post('/api/v1/users/record-login');
      return response.data;
    } catch (error) {
      // Don't throw - login recording is not critical
      console.warn('Failed to record login:', error);
    }
  };

  const copyUser = async (userId, copyData) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.post(`/api/v1/users/${userId}/copy`, copyData);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  // Daily Transactions API methods
  const getDailyTransactions = async (params = {}) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.get('/api/v1/daily-transactions', { params });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  const getTerminalDailyTransactions = async (terminalId, params = {}) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.get(`/api/v1/terminals/${encodeURIComponent(terminalId)}/transactions`, { params });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  const getTerminalTransactionSummary = async (terminalId, params = {}) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.get(`/api/v1/terminals/${encodeURIComponent(terminalId)}/transactions/summary`, { params });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  const getDailyTransactionsAnalytics = async (params = {}) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.get('/api/v1/daily-transactions/analytics', { params });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  // Generic API call method
  const apiCall = async (endpoint, method = 'GET', data = null, params = null) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      
      const config = {
        method: method.toLowerCase(),
        url: endpoint
      };
      
      if (data && (method.toUpperCase() === 'POST' || method.toUpperCase() === 'PUT')) {
        config.data = data;
      }
      
      if (params && method.toUpperCase() === 'GET') {
        config.params = params;
      }
      
      const response = await api(config);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  // Accounts API
  const getAccounts = async () => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.get('/api/v1/accounts');
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  // Live transaction ingestion methods (for external ATM servers)
  const ingestLiveTransaction = async (transactionData, apiKey) => {
    try {
      const api = await createApiClient();
      const response = await api.post('/api/v1/live-transactions/ingest', transactionData, {
        headers: {
          'X-API-Key': apiKey
        }
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };

  const ingestLiveTransactionBatch = async (batchData, apiKey) => {
    try {
      const api = await createApiClient();
      const response = await api.post('/api/v1/live-transactions/batch', batchData, {
        headers: {
          'X-API-Key': apiKey
        }
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };

  const getLiveTransactionIngestionHealth = async (apiKey) => {
    try {
      const api = await createApiClient();
      const response = await api.get('/api/v1/live-transactions/health', {
        headers: {
          'X-API-Key': apiKey
        }
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };

  // NACHA Export API
  const generateNachaFile = async (params) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.post('/api/v1/nacha/generate', params);
      return response.data;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };

  const getNachaSettings = async () => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.get('/api/v1/nacha/settings');
      return response.data?.settings || null;
    } catch (error) {
      // Settings might not exist yet
      console.log('No NACHA settings found');
      return null;
    }
  };

  const saveNachaSettings = async (settings) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.put('/api/v1/nacha/settings', { settings });
      return response.data;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };

  const validateNachaFile = async (content) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.post('/api/v1/nacha/validate', { content });
      return response.data;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };

  // Hierarchy API methods for market/store filtering
  const getHierarchyMarkets = async () => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.get('/api/v1/hierarchy/markets');
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  const getHierarchyStores = async (market) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const params = market ? { market } : {};
      const response = await api.get('/api/v1/hierarchy/stores', { params });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  // Get all stores grouped by market for hierarchy display
  const getHierarchyGrouped = async () => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();

      // Get full hierarchy in a single call
      const response = await api.get('/api/v1/hierarchy');
      const hierarchy = response.data?.hierarchy || {};

      // Transform hierarchy structure to expected format
      // hierarchy is { org: { market: [{ name, terminalCount }] } }
      const markets = [];
      const grouped = {};

      for (const org of Object.keys(hierarchy)) {
        for (const market of Object.keys(hierarchy[org])) {
          if (market && !markets.includes(market)) {
            markets.push(market);
          }
          if (!grouped[market]) {
            grouped[market] = [];
          }
          // Extract store names from the array of { name, terminalCount }
          const stores = hierarchy[org][market].map(s => s.name);
          grouped[market].push(...stores);
        }
      }

      // Sort markets alphabetically
      markets.sort();

      // Build channel (organization) → stores mapping
      const channels = [];
      const storesByChannel = {};
      for (const org of Object.keys(hierarchy)) {
        if (org && org !== 'Unassigned') {
          channels.push(org);
          storesByChannel[org] = [];
          for (const market of Object.keys(hierarchy[org])) {
            const stores = hierarchy[org][market].map(s => s.name);
            storesByChannel[org].push(...stores);
          }
        }
      }
      // Add Unassigned last if it exists
      if (hierarchy['Unassigned']) {
        channels.push('Unassigned');
        storesByChannel['Unassigned'] = [];
        for (const market of Object.keys(hierarchy['Unassigned'])) {
          const stores = hierarchy['Unassigned'][market].map(s => s.name);
          storesByChannel['Unassigned'].push(...stores);
        }
      }

      return { markets, grouped, channels, storesByChannel };
    } catch (error) {
      handleApiError(error);
    }
  };

  // Terminal Summary API - fast grouped view using materialized view
  const getTerminalsSummary = async () => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.get('/api/v1/terminals/summary');
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  // Get terminals for a specific store
  const getTerminalsByStore = async (storeName) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.get(`/api/v1/terminals/by-store/${encodeURIComponent(storeName)}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  // Channel Permission helpers — Phase 3

  const getUserChannelGrants = async (userId) => {
    ensureAuthenticated();
    const api = await createApiClient();
    try {
      const response = await api.get(`/api/v1/users/${userId}/channel-permissions`);
      return response.data.permissions || [];
    } catch (error) {
      const wrapped = new Error(
        error.response?.data?.error || error.message || 'Failed to load channel grants'
      );
      wrapped.status = error.response?.status;
      wrapped.original = error;
      throw wrapped;
    }
  };

  const grantUserChannel = async (userId, channel) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.post(`/api/v1/users/${userId}/channel-permissions`, { channel });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  const revokeUserChannel = async (userId, channel) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.delete(`/api/v1/users/${userId}/channel-permissions/${encodeURIComponent(channel)}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  // Diff-apply: grants new channels not in current set, revokes channels removed from current set.
  const setUserChannelGrants = async (userId, newChannels) => {
    try {
      ensureAuthenticated();
      const current = await getUserChannelGrants(userId);
      const currentSet = new Set(current.map(p => p.channel));
      const newSet = new Set(newChannels);

      const toAdd = [...newSet].filter(c => !currentSet.has(c));
      const toRemove = [...currentSet].filter(c => !newSet.has(c));

      const errors = [];
      let succeededAdds = 0;
      let succeededRemoves = 0;

      for (const channel of toAdd) {
        try {
          await grantUserChannel(userId, channel);
          succeededAdds++;
        } catch (err) {
          errors.push({ op: 'grant', channel, message: err.message || String(err) });
        }
      }

      for (const channel of toRemove) {
        try {
          await revokeUserChannel(userId, channel);
          succeededRemoves++;
        } catch (err) {
          errors.push({ op: 'revoke', channel, message: err.message || String(err) });
        }
      }

      if (errors.length > 0) {
        const e = new Error(
          `Channel grant update partially failed: ${succeededAdds}/${toAdd.length} added, ${succeededRemoves}/${toRemove.length} revoked. Errors: ${errors.map(x => `${x.op} ${x.channel}: ${x.message}`).join('; ')}`
        );
        e.partial = true;
        e.errors = errors;
        e.succeededAdds = succeededAdds;
        e.succeededRemoves = succeededRemoves;
        throw e;
      }

      return { added: succeededAdds, removed: succeededRemoves };
    } catch (err) {
      if (!err.partial) {
        console.error('setUserChannelGrants error:', err);
      }
      throw err;
    }
  };

  // Market Permission helpers — Phase 3

  const getUserMarketGrants = async (userId) => {
    ensureAuthenticated();
    const api = await createApiClient();
    try {
      const response = await api.get(`/api/v1/users/${userId}/market-permissions`);
      return response.data.permissions || [];
    } catch (error) {
      const wrapped = new Error(
        error.response?.data?.error || error.message || 'Failed to load market grants'
      );
      wrapped.status = error.response?.status;
      wrapped.original = error;
      throw wrapped;
    }
  };

  const grantUserMarket = async (userId, market) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.post(`/api/v1/users/${userId}/market-permissions`, { market });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  const revokeUserMarket = async (userId, market) => {
    try {
      ensureAuthenticated();
      const api = await createApiClient();
      const response = await api.delete(`/api/v1/users/${userId}/market-permissions/${encodeURIComponent(market)}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  // Diff-apply: grants new markets not in current set, revokes markets removed from current set.
  const setUserMarketGrants = async (userId, newMarkets) => {
    try {
      ensureAuthenticated();
      const current = await getUserMarketGrants(userId);
      const currentSet = new Set(current.map(p => p.market));
      const newSet = new Set(newMarkets);

      const toAdd = [...newSet].filter(m => !currentSet.has(m));
      const toRemove = [...currentSet].filter(m => !newSet.has(m));

      const errors = [];
      let succeededAdds = 0;
      let succeededRemoves = 0;

      for (const market of toAdd) {
        try {
          await grantUserMarket(userId, market);
          succeededAdds++;
        } catch (err) {
          errors.push({ op: 'grant', market, message: err.message || String(err) });
        }
      }

      for (const market of toRemove) {
        try {
          await revokeUserMarket(userId, market);
          succeededRemoves++;
        } catch (err) {
          errors.push({ op: 'revoke', market, message: err.message || String(err) });
        }
      }

      if (errors.length > 0) {
        const e = new Error(
          `Market grant update partially failed: ${succeededAdds}/${toAdd.length} added, ${succeededRemoves}/${toRemove.length} revoked. Errors: ${errors.map(x => `${x.op} ${x.market}: ${x.message}`).join('; ')}`
        );
        e.partial = true;
        e.errors = errors;
        e.succeededAdds = succeededAdds;
        e.succeededRemoves = succeededRemoves;
        throw e;
      }

      return { added: succeededAdds, removed: succeededRemoves };
    } catch (err) {
      if (!err.partial) {
        console.error('setUserMarketGrants error:', err);
      }
      throw err;
    }
  };

  // Memoize context value to prevent unnecessary re-renders of all consumers
  // Only recreate when user changes (login/logout)
  const value = useMemo(() => ({
    apiCall,
    getOverview,
    getTerminals,
    getTerminalDetails,
    getLocations,
    getLocationDetails,
    updateLocationClosingPeriod,
    getSettlementReport,
    uploadFile,
    getUploadHistory,
    // User Management API
    getUsers,
    getUserById,
    createUser,
    updateUser,
    deleteUser,
    resetUserPassword,
    recordLogin,
    copyUser,
    // Daily Transactions API
    getDailyTransactions,
    getTerminalDailyTransactions,
    getTerminalTransactionSummary,
    getDailyTransactionsAnalytics,
    // Live Transaction Ingestion API (for external servers)
    ingestLiveTransaction,
    ingestLiveTransactionBatch,
    getLiveTransactionIngestionHealth,
    // Accounts API
    getAccounts,
    // NACHA Export API
    generateNachaFile,
    getNachaSettings,
    saveNachaSettings,
    validateNachaFile,
    // Hierarchy API for market/store filtering
    getHierarchyMarkets,
    getHierarchyStores,
    getHierarchyGrouped,
    // Terminal Summary API - fast grouped view
    getTerminalsSummary,
    getTerminalsByStore,
    // Channel Permission APIs — Phase 3
    getUserChannelGrants,
    grantUserChannel,
    revokeUserChannel,
    setUserChannelGrants,
    // Market Permission APIs — Phase 3
    getUserMarketGrants,
    grantUserMarket,
    revokeUserMarket,
    setUserMarketGrants
  }), [user]);

  return (
    <ApiContext.Provider value={value}>
      {children}
    </ApiContext.Provider>
  );
};

export { ApiContext };